import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import '../../services/GlobalVariables';
import {
    Box, Card, CardContent, Typography, Stack, Button, Table, TableHead, TableBody, TableRow, TableCell,
    IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, LinearProgress,
    Autocomplete, Grid, Paper, Divider, Switch, FormControlLabel, CardMedia
} from '@mui/material';
import { Add, Edit, Delete, CheckCircle, Cancel, RemoveCircle, CloudUpload } from '@mui/icons-material';
import Navbar from '../setup/Navbar';
import {
    fetchAllDeals,
    createDeal,
    updateDeal,
    deleteDeal,
    toggleDealStatus,
    reset,
    clearSelectedDeal
} from '../../features/setup/dealsSlice';
import { getAllItems } from '../../features/StockManagement/itemsInformationSlice';
import { getAllICategories } from '../../features/GeneralManagement/itemCategorySlice';

export default function DealsManagement() {
    const dispatch = useDispatch();
    const { deals, isLoading, isError, isSuccess, message } = useSelector(state => state.deals);
    const { items } = useSelector(state => state.itemsInf);
    const { categories } = useSelector(state => state.ic);
    const { user } = useSelector(state => state.auth);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [formData, setFormData] = useState({
        dealName: '',
        dealPrice: '',
        dealDescription: '',
        categoryCode: null,
        isActive: true,
        taxApplicable: false,
        dealImage: null
    });
    const [imagePreview, setImagePreview] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [selectedItems, setSelectedItems] = useState([]);
    const [itemToAdd, setItemToAdd] = useState(null);
    const [itemQuantity, setItemQuantity] = useState(1);

    useEffect(() => {
        dispatch(fetchAllDeals());
        dispatch(getAllItems());
        dispatch(getAllICategories());
    }, [dispatch]);

    useEffect(() => {
        if (isSuccess && message) {
            alert(message);
            dispatch(reset());
            setDialogOpen(false);
            dispatch(fetchAllDeals());
            resetForm();
        }
        if (isError && message) {
            alert(message);
            dispatch(reset());
        }
    }, [isSuccess, isError, message, dispatch]);

    const resetForm = () => {
        setFormData({
            dealName: '',
            dealPrice: '',
            dealDescription: '',
            categoryCode: null,
            isActive: true,
            taxApplicable: false,
            dealImage: null
        });
        setImagePreview(null);
        setImageFile(null);
        setSelectedItems([]);
        setItemToAdd(null);
        setItemQuantity(1);
        setEditId(null);
    };

    const handleImageChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setImageFile(file);
            // Create preview
            const reader = new FileReader();
            reader.onload = (event) => {
                setImagePreview(event.target?.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = () => {
        setImageFile(null);
        setImagePreview(null);
        setFormData({ ...formData, dealImage: null });
    };

    const openDialog = (deal) => {
        if (deal) {
            setEditId(deal.dealID);
            setFormData({
                dealName: deal.dealName,
                dealPrice: deal.dealPrice,
                dealDescription: deal.dealDescription || '',
                categoryCode: deal.categoryCode,
                isActive: deal.isActive === 1,
                taxApplicable: deal.taxApplicable === 1,
                dealImage: deal.dealImage || null
            });
            // Set image preview if exists - construct URL from filename
            if (deal.dealImage) {
                setImagePreview(`${global.SET_API_URL}/dealsImage/${deal.dealImage}`);
            }
            // Parse deal items if available
            if (deal.items) {
                try {
                    const parsedItems = JSON.parse(deal.items);
                    setSelectedItems(parsedItems.map(item => ({
                        itemCode: item.itemCode,
                        itemName: item.itemName || items.find(i => i.itemCode === item.itemCode)?.itemDesc || item.itemCode,
                        quantity: item.quantity,
                        salesPrice: item.salesPrice || items.find(i => i.itemCode === item.itemCode)?.salesPrice || 0
                    })));
                } catch (e) {
                    setSelectedItems([]);
                }
            }
        } else {
            resetForm();
        }
        setDialogOpen(true);
    };

    const handleAddItem = () => {
        if (!itemToAdd) {
            alert('Please select an item');
            return;
        }
        if (itemQuantity <= 0) {
            alert('Quantity must be greater than 0');
            return;
        }
        // Check if item already exists
        const exists = selectedItems.find(i => i.itemCode === itemToAdd.itemCode);
        if (exists) {
            alert('Item already added. Remove it first to change quantity.');
            return;
        }
        setSelectedItems([...selectedItems, {
            itemCode: itemToAdd.itemCode,
            itemName: itemToAdd.itemDesc,
            quantity: itemQuantity,
            salesPrice: itemToAdd.salesPrice || 0
        }]);
        setItemToAdd(null);
        setItemQuantity(1);
    };

    const handleRemoveItem = (itemCode) => {
        setSelectedItems(selectedItems.filter(i => i.itemCode !== itemCode));
    };

    const handleSave = () => {
        if (!formData.dealName) {
            alert('Deal name is required');
            return;
        }
        if (!formData.dealPrice || parseFloat(formData.dealPrice) <= 0) {
            alert('Deal price must be greater than 0');
            return;
        }
        if (selectedItems.length === 0) {
            alert('Please add at least one item to the deal');
            return;
        }

        // Create FormData if there's a file
        let dataToSend;
        if (imageFile) {
            dataToSend = new FormData();
            dataToSend.append('dealName', formData.dealName);
            dataToSend.append('dealPrice', parseFloat(formData.dealPrice));
            dataToSend.append('dealDescription', formData.dealDescription);
            dataToSend.append('categoryCode', formData.categoryCode || null);
            dataToSend.append('isActive', formData.isActive ? 1 : 0);
            dataToSend.append('taxApplicable', formData.taxApplicable ? 1 : 0);
            dataToSend.append('branchID', user?.defaultBranchID || 1);
            dataToSend.append('cmpID', user?.cmpID || 1);
            dataToSend.append('createdBy', user?.userID || null);
            dataToSend.append('items', JSON.stringify(selectedItems.map(item => ({
                itemCode: item.itemCode,
                quantity: item.quantity
            }))));
            dataToSend.append('image', imageFile);
            
            // For edit, append dealID to FormData
            if (editId) {
                dataToSend.append('dealID', editId);
            }
        } else {
            // Send as JSON if no file
            dataToSend = {
                dealName: formData.dealName,
                dealPrice: parseFloat(formData.dealPrice),
                dealDescription: formData.dealDescription,
                categoryCode: formData.categoryCode || null,
                isActive: formData.isActive ? 1 : 0,
                taxApplicable: formData.taxApplicable ? 1 : 0,
                branchID: user?.defaultBranchID || 1,
                cmpID: user?.cmpID || 1,
                createdBy: user?.userID || null,
                items: selectedItems.map(item => ({
                    itemCode: item.itemCode,
                    quantity: item.quantity
                }))
            };
            
            // For edit, add dealID to object
            if (editId) {
                dataToSend.dealID = editId;
            }
        }

        if (editId) {
            dispatch(updateDeal(dataToSend));
        } else {
            dispatch(createDeal(dataToSend));
        }
    };

    const handleStatusToggle = (deal) => {
        const nextStatus = deal.isActive === 1 ? 0 : 1;
        dispatch(toggleDealStatus({ dealID: deal.dealID, isActive: nextStatus }));
    };

    const handleDelete = (dealID) => {
        if (window.confirm('Delete this deal? This will also remove all associated items.')) {
            dispatch(deleteDeal(dealID));
        }
    };

    const calculateItemsTotal = () => {
        return selectedItems.reduce((sum, item) => {
            return sum + (parseFloat(item.salesPrice || 0) * item.quantity);
        }, 0);
    };

    const getSavingsAmount = () => {
        const itemsTotal = calculateItemsTotal();
        const dealPrice = parseFloat(formData.dealPrice || 0);
        return itemsTotal - dealPrice;
    };

    return (
        <>
            <Navbar />
            <Box sx={{ p: 2 }}>
                {isLoading && <LinearProgress />}
                <Card>
                    <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="h5" fontWeight="bold">Deals Management</Typography>
                            <Button startIcon={<Add />} variant="contained" onClick={() => openDialog(null)}>
                                Add Deal
                            </Button>
                        </Stack>

                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>Deal Name</strong></TableCell>
                                    <TableCell><strong>Description</strong></TableCell>
                                    <TableCell><strong>Category</strong></TableCell>
                                    <TableCell align="right"><strong>Price</strong></TableCell>
                                    <TableCell><strong>Tax</strong></TableCell>
                                    <TableCell><strong>Status</strong></TableCell>
                                    <TableCell align="center"><strong>Actions</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {deals.map(deal => (
                                    <TableRow key={deal.dealID}>
                                        <TableCell>{deal.dealName}</TableCell>
                                        <TableCell>{deal.dealDescription || '-'}</TableCell>
                                        <TableCell>{deal.categoryName || '-'}</TableCell>
                                        <TableCell align="right">Rs. {parseFloat(deal.dealPrice || 0).toFixed(2)}</TableCell>
                                        <TableCell>
                                            <Chip 
                                                size="small" 
                                                label={deal.taxApplicable === 1 ? 'Yes' : 'No'} 
                                                color={deal.taxApplicable === 1 ? 'success' : 'default'} 
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={deal.isActive === 1 ? 'Active' : 'Inactive'}
                                                color={deal.isActive === 1 ? 'success' : 'default'}
                                                icon={deal.isActive === 1 ? <CheckCircle /> : <Cancel />}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Stack direction="row" spacing={1} justifyContent="center">
                                                <IconButton color="primary" onClick={() => openDialog(deal)}>
                                                    <Edit />
                                                </IconButton>
                                                <IconButton
                                                    color={deal.isActive === 1 ? 'warning' : 'success'}
                                                    onClick={() => handleStatusToggle(deal)}
                                                    title={deal.isActive === 1 ? 'Deactivate' : 'Activate'}
                                                >
                                                    {deal.isActive === 1 ? <Cancel /> : <CheckCircle />}
                                                </IconButton>
                                                <IconButton color="error" onClick={() => handleDelete(deal.dealID)}>
                                                    <Delete />
                                                </IconButton>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {deals.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            <Typography variant="body2" color="text.secondary">
                                                No deals found. Click "Add Deal" to create one.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>

                {/* Add/Edit Dialog */}
                <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
                    <DialogTitle>{editId ? 'Edit Deal' : 'Add New Deal'}</DialogTitle>
                    <DialogContent>
                        <Stack spacing={2} sx={{ mt: 1 }}>
                            <TextField
                                label="Deal Name"
                                value={formData.dealName}
                                onChange={(e) => setFormData({ ...formData, dealName: e.target.value })}
                                fullWidth
                                required
                            />
                            <TextField
                                label="Description"
                                value={formData.dealDescription}
                                onChange={(e) => setFormData({ ...formData, dealDescription: e.target.value })}
                                fullWidth
                                multiline
                                rows={2}
                            />
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <TextField
                                        label="Deal Price"
                                        type="number"
                                        value={formData.dealPrice}
                                        onChange={(e) => setFormData({ ...formData, dealPrice: e.target.value })}
                                        fullWidth
                                        required
                                        inputProps={{ min: 0, step: 0.01 }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <Autocomplete
                                        options={categories || []}
                                        getOptionLabel={(option) => option.cateDescription || ''}
                                        value={categories?.find(c => c.cateCode === formData.categoryCode) || null}
                                        onChange={(e, newValue) => setFormData({ ...formData, categoryCode: newValue?.cateCode || null })}
                                        renderInput={(params) => <TextField {...params} label="Category" />}
                                    />
                                </Grid>
                            </Grid>

                            <Stack direction="row" spacing={2}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.isActive}
                                            onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                        />
                                    }
                                    label="Active"
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.taxApplicable}
                                            onChange={(e) => setFormData({ ...formData, taxApplicable: e.target.checked })}
                                        />
                                    }
                                    label="Tax Applicable"
                                />
                            </Stack>

                            {/* Image Upload Section */}
                            <Paper elevation={1} sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Deal Image</Typography>
                                {imagePreview && (
                                    <Box sx={{ mb: 2, textAlign: 'center' }}>
                                        <CardMedia
                                            component="img"
                                            src={imagePreview}
                                            alt="Deal preview"
                                            sx={{
                                                width: '100%',
                                                maxWidth: 200,
                                                height: 200,
                                                objectFit: 'cover',
                                                borderRadius: 2,
                                                margin: '0 auto',
                                                mb: 1
                                            }}
                                        />
                                        <Button
                                            size="small"
                                            color="error"
                                            onClick={handleRemoveImage}
                                            variant="outlined"
                                        >
                                            Remove Image
                                        </Button>
                                    </Box>
                                )}
                                {!imagePreview && (
                                    <Box>
                                        <input
                                            accept="image/*"
                                            type="file"
                                            id="deal-image-input"
                                            style={{ display: 'none' }}
                                            onChange={handleImageChange}
                                        />
                                        <label htmlFor="deal-image-input">
                                            <Button
                                                variant="outlined"
                                                component="span"
                                                startIcon={<CloudUpload />}
                                                fullWidth
                                            >
                                                Upload Image
                                            </Button>
                                        </label>
                                    </Box>
                                )}
                            </Paper>

                            <Divider />
                            <Typography variant="h6">Deal Items</Typography>

                            {/* Add Item Section */}
                            <Paper elevation={1} sx={{ p: 2 }}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={6}>
                                        <Autocomplete
                                            options={items || []}
                                            getOptionLabel={(option) => `${option.itemDesc} (${option.itemCode})`}
                                            value={itemToAdd}
                                            onChange={(e, newValue) => setItemToAdd(newValue)}
                                            renderInput={(params) => <TextField {...params} label="Select Item" size="small" />}
                                        />
                                    </Grid>
                                    <Grid item xs={3}>
                                        <TextField
                                            label="Quantity"
                                            type="number"
                                            value={itemQuantity}
                                            onChange={(e) => setItemQuantity(parseInt(e.target.value) || 1)}
                                            size="small"
                                            fullWidth
                                            inputProps={{ min: 1 }}
                                        />
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Button variant="contained" onClick={handleAddItem} fullWidth>
                                            Add Item
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Paper>

                            {/* Selected Items List */}
                            {selectedItems.length > 0 && (
                                <Paper elevation={1} sx={{ p: 2 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Selected Items:</Typography>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Item</TableCell>
                                                <TableCell align="center">Qty</TableCell>
                                                <TableCell align="right">Price</TableCell>
                                                <TableCell align="right">Total</TableCell>
                                                <TableCell align="center">Action</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {selectedItems.map(item => (
                                                <TableRow key={item.itemCode}>
                                                    <TableCell>{item.itemName}</TableCell>
                                                    <TableCell align="center">{item.quantity}</TableCell>
                                                    <TableCell align="right">Rs. {parseFloat(item.salesPrice || 0).toFixed(2)}</TableCell>
                                                    <TableCell align="right">Rs. {(parseFloat(item.salesPrice || 0) * item.quantity).toFixed(2)}</TableCell>
                                                    <TableCell align="center">
                                                        <IconButton size="small" color="error" onClick={() => handleRemoveItem(item.itemCode)}>
                                                            <RemoveCircle />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            <TableRow>
                                                <TableCell colSpan={3} align="right"><strong>Items Total:</strong></TableCell>
                                                <TableCell align="right"><strong>Rs. {calculateItemsTotal().toFixed(2)}</strong></TableCell>
                                                <TableCell></TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell colSpan={3} align="right"><strong>Deal Price:</strong></TableCell>
                                                <TableCell align="right">
                                                    <strong style={{ color: '#1976d2' }}>
                                                        Rs. {parseFloat(formData.dealPrice || 0).toFixed(2)}
                                                    </strong>
                                                </TableCell>
                                                <TableCell></TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell colSpan={3} align="right"><strong>Savings:</strong></TableCell>
                                                <TableCell align="right">
                                                    <strong style={{ color: getSavingsAmount() > 0 ? '#2e7d32' : '#d32f2f' }}>
                                                        Rs. {getSavingsAmount().toFixed(2)}
                                                    </strong>
                                                </TableCell>
                                                <TableCell></TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </Paper>
                            )}
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleSave} variant="contained" disabled={isLoading}>
                            {editId ? 'Update' : 'Create'} Deal
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </>
    );
}
